import { useMemo } from 'react';
import { buildTimeline, connectorHeight, maxGap, totalElapsedMs } from '../lib/deliveries';
import {
  formatClock,
  formatCountdown,
  formatDuration,
  formatDurationShort,
  formatFailureKind,
  toEpoch,
} from '../lib/format';
import type { Attempt, DeliveryWithAttempts } from '../types';
import { CopyButton } from './CopyButton';
import { AttemptStatusBadge, DeliveryStatusBadge } from './StatusBadge';

/** One attempt's outcome in the terms a reader cares about: status code or failure kind. */
function describeOutcome(attempt: Attempt): string {
  if (attempt.status === 'succeeded') {
    return attempt.responseStatus !== null ? `HTTP ${attempt.responseStatus}` : 'Succeeded';
  }
  if (attempt.responseStatus !== null) {
    return `HTTP ${attempt.responseStatus}`;
  }
  return formatFailureKind(attempt.failureKind);
}

function AttemptNode({ attempt }: { attempt: Attempt }) {
  const failed = attempt.status !== 'succeeded';

  return (
    <div className={`attempt attempt--${failed ? 'failed' : 'succeeded'}`}>
      <span className="attempt__marker" aria-hidden="true">
        {attempt.attemptNumber}
      </span>

      <div className="attempt__card">
        <div className="attempt__head">
          <span className="attempt__title">Attempt {attempt.attemptNumber}</span>
          <AttemptStatusBadge status={attempt.status} />
          <span className="attempt__outcome mono">{describeOutcome(attempt)}</span>
          <span className="attempt__duration mono">{formatDuration(attempt.requestDurationMs)}</span>
        </div>

        <div className="attempt__meta">
          <span title="When this request was sent">{formatClock(attempt.startedAt)}</span>
          <span aria-hidden="true">·</span>
          <span title="Time from event ingestion to the end of this attempt">
            {formatDuration(attempt.elapsedSinceEventMs)} since event
          </span>
          {failed && attempt.failureKind ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{formatFailureKind(attempt.failureKind)}</span>
            </>
          ) : null}
        </div>

        {attempt.errorMessage ? <p className="attempt__error mono">{attempt.errorMessage}</p> : null}
      </div>
    </div>
  );
}

interface DeliveryTimelineProps {
  delivery: DeliveryWithAttempts;
  eventCreatedAt: string;
  /** Ticking clock, so a pending retry counts down. */
  now: number;
}

export function DeliveryTimeline({ delivery, eventCreatedAt, now }: DeliveryTimelineProps) {
  const attempts = useMemo(() => delivery.attempts ?? [], [delivery.attempts]);
  const nodes = useMemo(() => buildTimeline(attempts, eventCreatedAt), [attempts, eventCreatedAt]);
  const largestGap = useMemo(() => maxGap(nodes), [nodes]);

  const elapsed = totalElapsedMs(delivery, attempts);
  const succeeded = delivery.status === 'succeeded';

  const backoffs = nodes
    .filter((node) => !node.isFirst && node.gapMs !== null)
    .map((node) => formatDurationShort(node.gapMs));

  const nextAttemptEpoch = toEpoch(delivery.nextAttemptAt);

  return (
    <article className="delivery">
      <header className="delivery__head">
        <div className="delivery__ident">
          <DeliveryStatusBadge status={delivery.status} />
          <span className="delivery__url mono" title={delivery.url}>
            {delivery.url}
          </span>
          <CopyButton value={delivery.url} label="Copy URL" />
        </div>

        <dl className="delivery__facts">
          <div className="fact">
            <dt>Attempts</dt>
            <dd className="mono">
              {delivery.attemptCount} / {delivery.maxAttempts}
            </dd>
          </div>
          <div className="fact">
            <dt>{succeeded ? 'End-to-end latency' : 'Elapsed'}</dt>
            <dd className="mono">{formatDuration(elapsed)}</dd>
          </div>
          <div className="fact">
            <dt>Endpoint</dt>
            <dd className="mono">{delivery.endpointId}</dd>
          </div>
          <div className="fact">
            <dt>Delivery</dt>
            <dd className="mono">{delivery.id}</dd>
          </div>
        </dl>
      </header>

      {backoffs.length >= 2 ? (
        <p className="delivery__backoff">
          <span className="delivery__backoff-label">Backoff</span>
          <span className="mono">{backoffs.join('  →  ')}</span>
        </p>
      ) : null}

      {delivery.status === 'pending' && nextAttemptEpoch !== null ? (
        <p className="delivery__next">
          <span className="pulse" aria-hidden="true" />
          Next attempt {formatCountdown(nextAttemptEpoch - now)}
          <span className="mono"> · {formatClock(delivery.nextAttemptAt)}</span>
        </p>
      ) : null}

      {nodes.length === 0 ? (
        <p className="delivery__none">
          No attempts recorded yet — this delivery is queued and has not been picked up by a worker.
        </p>
      ) : (
        <ol className="timeline">
          {nodes.map((node) => (
            <li key={node.attempt.id} className="timeline__row">
              <div
                className="timeline__gap"
                style={{ height: connectorHeight(node.gapMs, largestGap) }}
                title={
                  node.isFirst
                    ? 'Time spent in the queue before the first attempt'
                    : 'Backoff delay before this retry'
                }
              >
                <span className="timeline__rail" aria-hidden="true" />
                <span className="timeline__gap-label">
                  <span className="timeline__gap-kind">{node.isFirst ? 'queued' : 'backoff'}</span>
                  <span className="mono">{formatDurationShort(node.gapMs)}</span>
                </span>
              </div>
              <AttemptNode attempt={node.attempt} />
            </li>
          ))}
        </ol>
      )}

      {delivery.status === 'dead_lettered' ? (
        <p className="delivery__dead">
          <strong>Dead-lettered</strong> after {delivery.attemptCount} attempt
          {delivery.attemptCount === 1 ? '' : 's'}.
          {delivery.lastError ? <span className="mono"> {delivery.lastError}</span> : null}
        </p>
      ) : null}
    </article>
  );
}
